import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import config from '../../config';
import {
  saveProjectDetails,
  loadProject,
  reset,
  revertProjectChanges,
  updateProjectInfo,
  selectProjectSuggestion,
} from '../../actions/eproject/main';
import {
  loadUsers,
  insertUserRow,
  switchUserRowToEdit,
  updateUserText,
  selectUserSuggestion,
  deleteUserRow,
} from '../../actions/eproject/user';
import {
  loadHarvcoTags,
  insertHarvcoTagRow,
  switchHarvcoTagRowToEdit,
  updateHarvcoTag,
  deleteHarvcoTagRow,
} from '../../actions/eproject/harvcoTag';
import { loadIndustries, updateIndustryTags } from '../../actions/eproject/industry';
import { openInternalLink } from '../../utils/url';
import { showError } from '../../utils/MessagePopup';
import { getIn } from '../../utils/ChangeSpy';
import deletionCheck from '../../utils/deletingPermission';
import { usePopupContext } from '../../contexts/PopupContext';

const types = config.values.getIn(['eproject', 'types']);

/**
 * Container of the popup for editing executive project details. Popups do not stack, so every popup opened from
 * here (industry tags, permission alerts) returns to this one when it is closed.
 * Closing this popup discards unsaved changes by reloading the edited data, then calls `onClose` from popup props.
 */
const EProjectDetailsPopupContainer = props => {
  const {
    common,
    deleteHarvcoTagRow,
    deleteUserRow,
    harvcoTags,
    insertHarvcoTagRow,
    insertUserRow,
    loadHarvcoTags,
    loadIndustries,
    loadProject,
    loadUsers,
    onClose,
    projectData,
    reset,
    revertProjectChanges,
    saveProjectDetails,
    selectProjectSuggestion,
    selectUserSuggestion,
    switchHarvcoTagRowToEdit,
    switchUserRowToEdit,
    updateHarvcoTag,
    updateIndustryTags,
    updateProjectInfo,
    updateUserText,
    user,
    users,
  } = props;

  const { openPopup, closePopup, onClosePopup } = usePopupContext();
  const [isSaving, setIsSaving] = useState(false);

  const projectId = projectData.get('id');

  const reopenPopup = useCallback(() => {
    openPopup('EProjectDetailsPopup', { onClose });
  }, [openPopup, onClose]);

  const openChildPopup = useCallback(
    (name, popupProps) => {
      openPopup(name, popupProps);
      onClosePopup(reopenPopup);
    },
    [openPopup, onClosePopup, reopenPopup],
  );

  const handleDiscard = useCallback(() => {
    reset();
    revertProjectChanges();
    Promise.all([
      loadProject(projectId),
      loadUsers(projectId),
      loadHarvcoTags(projectId),
      loadIndustries(projectId),
    ]).then(() => {
      if (onClose) {
        onClose();
      }
    });
  }, [reset, revertProjectChanges, loadProject, loadUsers, loadHarvcoTags, loadIndustries, projectId, onClose]);

  useEffect(() => {
    onClosePopup(handleDiscard);
  }, [onClosePopup, handleDiscard]);

  const handleSave = useCallback(() => {
    const tags = getIn(common, 'selectedBICs');
    const models = getIn(common, 'selectedBMs');

    if (!tags.size || !models.size) {
      showError(openChildPopup, ['Industry tags or business models are missing'], reopenPopup);

      return;
    }

    setIsSaving(true);
    saveProjectDetails().then(isSaved => {
      setIsSaving(false);
      if (isSaved) {
        closePopup();
      }
    });
  }, [common, openChildPopup, reopenPopup, saveProjectDetails, closePopup]);

  const handleTextChange = useCallback(
    ({ event, data: { type, row } }) => {
      const {
        target: { name, value, checked },
      } = event;

      if (type === types.get('USERS')) {
        updateUserText(row.get('id'), value);
      } else if (type === types.get('PROJECT')) {
        updateProjectInfo(name, event.target.type.toUpperCase() === 'CHECKBOX' ? checked : value);
      } else if (type === types.get('HARVCO_TAGS')) {
        updateHarvcoTag(row.get('index'), name, value);
      }
    },
    [updateUserText, updateProjectInfo, updateHarvcoTag],
  );

  const handleFocus = useCallback(
    (name, value = '') => {
      updateProjectInfo(name, value);
    },
    [updateProjectInfo],
  );

  const handleSuggestionClose = useCallback(
    ({ data }) => {
      const { type, row } = data;

      if (type === 'users') {
        switchUserRowToEdit(row.get('id'));
      }

      if (type === 'project') {
        reset();
      }
    },
    [switchUserRowToEdit, reset],
  );

  const handleSuggestionSelect = useCallback(
    ({ data, suggestion }) => {
      const { type, row, name } = data;

      if (type === types.get('USERS')) {
        selectUserSuggestion(row.get('id'), suggestion.get('id'), suggestion.get('name'));
      } else if (type === types.get('PROJECT')) {
        selectProjectSuggestion(name, suggestion.get('id'), suggestion.get('name'));
      }
    },
    [selectUserSuggestion, selectProjectSuggestion],
  );

  const handleSuggestionInputClick = useCallback(
    ({ event }) => {
      event.stopPropagation();
      reset();
    },
    [reset],
  );

  const handleRowClick = useCallback(
    ({ row, type }) => {
      if (type === types.get('USERS')) {
        switchUserRowToEdit(row.get('id'));
      } else if (type === types.get('HARVCO_TAGS')) {
        switchHarvcoTagRowToEdit(row.get('index'));
      }
    },
    [switchUserRowToEdit, switchHarvcoTagRowToEdit],
  );

  const handleRowDoubleClick = useCallback(({ row, type }) => {
    if (type === types.get('USERS')) {
      openInternalLink(`/user/${row.get('id')}`);
    }
  }, []);

  const handleRowInsert = useCallback(
    type => {
      if (type === types.get('USERS')) {
        insertUserRow();
      } else if (type === types.get('INDUSTRIES')) {
        const tags = getIn(common, 'selectedBICs');
        const models = getIn(common, 'selectedBMs');

        openChildPopup('TagsManagementPopup', {
          addNew: false,
          clientView: true,
          selectedTags: tags.reduce((accumulator, tag) => {
            accumulator[tag] = true;

            return accumulator;
          }, {}),
          selectedModels: models.reduce((accumulator, model) => {
            accumulator[model] = true;

            return accumulator;
          }, {}),
          callback: (tagSelecteds, modelSelecteds) => updateIndustryTags(tagSelecteds, modelSelecteds),
        });
      } else if (type === types.get('HARVCO_TAGS')) {
        insertHarvcoTagRow();
      }
    },
    [insertUserRow, common, openChildPopup, updateIndustryTags, insertHarvcoTagRow],
  );

  const deleteRowAfterConfirm = useCallback(
    (event, { id, type }) => {
      if (type === types.get('USERS')) {
        deleteUserRow(id);
      } else if (type === types.get('HARVCO_TAGS')) {
        deleteHarvcoTagRow(id);
      }
    },
    [deleteUserRow, deleteHarvcoTagRow],
  );

  const handleDeleteRow = useMemo(() => deletionCheck(user, deleteRowAfterConfirm, { openPopup: openChildPopup }), [
    user,
    deleteRowAfterConfirm,
    openChildPopup,
  ]);

  const handleKeyPress = useCallback(
    ({ event }) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        reset();
      }
    },
    [reset],
  );

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <Popups.EProjectDetailsPopup
      common={common}
      harvcoTags={harvcoTags}
      isSaving={isSaving}
      onCancel={closePopup}
      onDeleteRow={handleDeleteRow}
      onFocus={handleFocus}
      onKeyPress={handleKeyPress}
      onReset={handleReset}
      onRowClick={handleRowClick}
      onRowDoubleClick={handleRowDoubleClick}
      onRowInsert={handleRowInsert}
      onSave={handleSave}
      onSuggestionClose={handleSuggestionClose}
      onSuggestionInputClick={handleSuggestionInputClick}
      onSuggestionSelect={handleSuggestionSelect}
      onTextChange={handleTextChange}
      projectData={projectData}
      users={users}
    />
  );
};

EProjectDetailsPopupContainer.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  harvcoTags: PropTypes.instanceOf(List).isRequired,
  onClose: PropTypes.func,
  projectData: PropTypes.instanceOf(Map).isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = (state, props) => ({
  common: state.eproject.common,
  harvcoTags: state.eproject.harvcoTags,
  onClose: props.popup.getIn(['props', 'onClose']),
  projectData: state.eproject.projectData,
  user: state.auth.get('user', null),
  users: state.eproject.users,
});

export { EProjectDetailsPopupContainer };
export default connect(
  mapStateToProps,
  {
    saveProjectDetails,
    loadProject,
    reset,
    revertProjectChanges,
    updateProjectInfo,
    selectProjectSuggestion,
    loadUsers,
    insertUserRow,
    switchUserRowToEdit,
    updateUserText,
    selectUserSuggestion,
    deleteUserRow,
    loadHarvcoTags,
    insertHarvcoTagRow,
    switchHarvcoTagRowToEdit,
    updateHarvcoTag,
    deleteHarvcoTagRow,
    loadIndustries,
    updateIndustryTags,
  },
  mergeProps,
  connectOptions,
)(memo(EProjectDetailsPopupContainer));
